import { AppState } from "../types";
import { Opportunity } from "../types/opportunity";

export type GuardResult = {
  passed: boolean;
  failures: string[];
  warnings: string[];
  recommendations: string[];
};

/**
 * The PanelGuard: Non-bypassable core rules governed by the Expert Panel.
 * This ensures architectural and product integrity before critical actions.
 */
export const PanelGuard = {
  /**
   * Rule 1 (Privacy Architect): Local-First Trust Posture.
   * Sensitive profiles must not be included in cloud sync unless explicitly approved.
   */
  validatePrivacyPosture(state: AppState): GuardResult {
    const failures: string[] = [];
    const sensitiveInCloud = state.sensitiveSupportProfiles.filter(
      (profile) => {
        if (!profile.include_in_export) {
          return false;
        }

        const opportunity = state.opportunities.find(
          (item) => item.opportunity_id === profile.opportunity_id,
        );
        const accountPolicy = opportunity
          ? state.enterpriseControlProfiles.find((item) => item.account_id === opportunity.account_id)
          : undefined;

        return !accountPolicy?.allow_sensitive_support_export;
      },
    );

    if (sensitiveInCloud.length > 0) {
      failures.push(
        "Privacy Violation: Sensitive profiles marked for export but account policy forbids it.",
      );
    }

    return {
      passed: failures.length === 0,
      failures,
      warnings: [],
      recommendations: ["Ensure 'Sovereign Opt-out' is active for high-risk candidates."],
    };
  },

  /**
   * Rule 2 (QA / Evidence Lead): Proof Artifact Integrity.
   * A "Play" cannot be advanced to 'outreach_ready' without at least one resume and one JD.
   */
  validateEvidenceDepth(opportunity: Opportunity, state: AppState): GuardResult {
    const failures: string[] = [];
    const proof = state.proof.filter((p) => p.opportunity_id === opportunity.opportunity_id);

    const hasResume = proof.some((p) => p.proof_type === "resume");
    const hasJD = proof.some((p) => p.proof_type === "job_description");

    if (
      opportunity.current_stage === "outreach_ready" ||
      opportunity.current_stage === "interview_active"
    ) {
      if (!hasResume)
        failures.push("Evidence Gap: No resume artifact found for an active outreach play.");
      if (!hasJD)
        failures.push(
          "Evidence Gap: No job description artifact found for an active outreach play.",
        );
    }

    return {
      passed: failures.length === 0,
      failures,
      warnings: [],
      recommendations: ["Upload original PDF artifacts to increase evidence weight."],
    };
  },

  /**
   * Rule 3 (CTO / Architect): Technical Debt & Schema Integrity.
   * Prevent exports if the schema version is mismatched or corrupted.
   */
  validateSchemaIntegrity(state: AppState): GuardResult {
    const failures: string[] = [];
    if (!state.schemaVersion || typeof state.schemaVersion !== "string") {
      failures.push("Schema Corruption: Missing or invalid schema version.");
    }
    return {
      passed: failures.length === 0,
      failures,
      warnings: [],
      recommendations: ["Run 'pnpm run migrate' if version mismatch persists."],
    };
  },

  /**
   * Comprehensive check before ZIP export.
   */
  canExport(opportunityId: string, state: AppState): GuardResult {
    const opportunity = state.opportunities.find((o) => o.opportunity_id === opportunityId);
    if (!opportunity)
      return {
        passed: false,
        failures: ["Opportunity not found"],
        warnings: [],
        recommendations: [],
      };

    const privacy = this.validatePrivacyPosture(state);
    const evidence = this.validateEvidenceDepth(opportunity, state);
    const schema = this.validateSchemaIntegrity(state);

    const allFailures = [...privacy.failures, ...evidence.failures, ...schema.failures];
    const allWarnings = [...privacy.warnings, ...evidence.warnings, ...schema.warnings];
    const allRecommendations = [
      ...privacy.recommendations,
      ...evidence.recommendations,
      ...schema.recommendations,
    ];

    return {
      passed: allFailures.length === 0,
      failures: allFailures,
      warnings: allWarnings,
      recommendations: allRecommendations,
    };
  },
};
